import React, { useState } from 'react'

const CONVERT_TYPES = [
  { value: 'image', label: '이미지(PNG/JPG/GIF) → WEBP', accept: 'image/*' },
  { value: 'mp4Anim', label: 'MP4 → 애니메이션 WEBP', accept: 'video/mp4,.mp4' },
  { value: 'mp4Thumb', label: 'MP4 → 썸네일 WEBP(첫 프레임)', accept: 'video/mp4,.mp4' },
]

const formatBytes = bytes => {
  if (!bytes && bytes !== 0) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

const WebpConverter = () => {
  const [sourceType, setSourceType] = useState('file') // 'file' | 'url'
  const [convertType, setConvertType] = useState('image')
  const [file, setFile] = useState(null)
  const [url, setUrl] = useState('')

  const [quality, setQuality] = useState(75)
  const [fps, setFps] = useState(12)
  const [width, setWidth] = useState('')
  const [height, setHeight] = useState('')
  const [duration, setDuration] = useState('')

  const [output, setOutput] = useState('file') // 'file' | 's3'
  const [s3Key, setS3Key] = useState('')

  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null) // { objectUrl, inputSize, outputSize, filename }

  const isImage = convertType === 'image'
  const isMp4Anim = convertType === 'mp4Anim'
  const currentType = CONVERT_TYPES.find(t => t.value === convertType)
  const currentAccept = (currentType && currentType.accept) || '*/*'

  const onChangeConvertType = value => {
    setConvertType(value)
    setFile(null)
    setResult(null)
  }

  const buildDefaultS3Key = () => {
    let base = ''
    if (sourceType === 'url' && url.trim()) {
      try {
        const p = new URL(url.trim()).pathname.replace(/^\/+/, '')
        base = p.replace(/\.[^.]+$/, '')
      } catch (e) {}
    } else if (sourceType === 'file' && file) {
      base = file.name.replace(/\.[^.]+$/, '')
    }
    if (!base) return ''
    if (convertType === 'mp4Thumb') base += '_thumbnail'
    return base + '.webp'
  }

  const onChangeOutput = value => {
    setOutput(value)
    setResult(null)
    if (value === 's3' && !s3Key) {
      setS3Key(buildDefaultS3Key())
    }
  }

  const convert = async () => {
    if (sourceType === 'file' && !file) {
      window.alert('변환할 파일을 선택해주세요.')
      return
    }
    if (sourceType === 'url' && !url.trim()) {
      window.alert('변환할 S3 URL을 입력해주세요.')
      return
    }
    if (output === 's3' && !s3Key.trim()) {
      window.alert('S3 업로드 경로(Key)를 입력해주세요.')
      return
    }

    setLoading(true)
    if (result && result.objectUrl) URL.revokeObjectURL(result.objectUrl)
    setResult(null)

    try {
      const formData = new FormData()
      formData.append('sourceType', sourceType)
      formData.append('convertType', convertType)
      formData.append('quality', String(quality))
      formData.append('fps', String(fps))
      if (width) formData.append('width', String(width))
      if (height) formData.append('height', String(height))
      if (isMp4Anim && duration) formData.append('duration', String(duration))
      formData.append('output', output)
      if (output === 's3') formData.append('s3Key', s3Key.trim())
      if (sourceType === 'file') {
        formData.append('file', file)
      } else {
        formData.append('url', url.trim())
      }

      const response = await fetch('/new/webAdmin/tool/webp/convert', {
        method: 'POST',
        headers: {
          token: sessionStorage.getItem('token'),
        },
        body: formData,
      })

      if (!response.ok) {
        let message = `변환 실패 (${response.status})`
        try {
          const data = await response.json()
          if (data && data.message) message = `변환 실패: ${data.message}`
        } catch (e) {}
        window.alert(message)
        setLoading(false)
        return
      }

      let filename = 'output.webp'
      if (sourceType === 'file' && file) {
        filename = file.name.replace(/\.[^.]+$/, '') + '.webp'
      } else if (sourceType === 'url') {
        try {
          const p = new URL(url.trim()).pathname
          const base = p.substring(p.lastIndexOf('/') + 1).replace(/\.[^.]+$/, '')
          if (base) filename = base + '.webp'
        } catch (e) {}
      }

      // NDJSON 스트림 읽기: processing 하트비트를 무시하고 done/failed 를 기다린다.
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let finalMsg = null

      const handleLine = line => {
        const trimmed = line.trim()
        if (!trimmed) return
        let obj
        try {
          obj = JSON.parse(trimmed)
        } catch (e) {
          return
        }
        if (obj.state === 'done' || obj.state === 'failed') finalMsg = obj
      }

      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        let idx
        while ((idx = buffer.indexOf('\n')) >= 0) {
          handleLine(buffer.slice(0, idx))
          buffer = buffer.slice(idx + 1)
        }
      }
      if (buffer) handleLine(buffer)

      if (!finalMsg) {
        window.alert('변환 실패: 서버 응답이 완료되지 않았습니다.')
        setLoading(false)
        return
      }
      if (finalMsg.state === 'failed') {
        window.alert('변환 실패: ' + (finalMsg.message || 'convert error'))
        setLoading(false)
        return
      }

      if (finalMsg.filename) filename = finalMsg.filename

      if (finalMsg.isFile) {
        const bin = atob(finalMsg.dataBase64)
        const bytes = new Uint8Array(bin.length)
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
        const blob = new Blob([bytes], { type: 'image/webp' })
        const objectUrl = URL.createObjectURL(blob)
        setResult({
          objectUrl,
          inputSize: finalMsg.inputSize || 0,
          outputSize: finalMsg.outputSize || blob.size,
          filename,
        })
      } else {
        setResult({
          s3Url: finalMsg.url,
          inputSize: finalMsg.inputSize || 0,
          outputSize: finalMsg.outputSize || 0,
          filename,
        })
      }
    } catch (err) {
      console.error('[webp convert] error:', err)
      window.alert('변환 중 오류가 발생했습니다: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const download = () => {
    if (!result) return
    const a = document.createElement('a')
    a.href = result.objectUrl
    a.download = result.filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const savedRatio =
    result && result.inputSize
      ? Math.round((1 - result.outputSize / result.inputSize) * 100)
      : null

  return (
    <div style={styles.wrapper}>
      <h3 style={{ marginBottom: 24 }}>WEBP 변환</h3>

      <div style={styles.card}>
        <div style={styles.label}>변환 타입</div>
        <div style={styles.radioRow}>
          {CONVERT_TYPES.map(t => (
            <label key={t.value} style={styles.radioLabel}>
              <input
                type="radio"
                name="convertType"
                checked={convertType === t.value}
                onChange={() => onChangeConvertType(t.value)}
              />
              <span style={{ marginLeft: 6 }}>{t.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.label}>입력 방식</div>
        <div style={styles.radioRow}>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="sourceType"
              checked={sourceType === 'file'}
              onChange={() => {
                setSourceType('file')
                setResult(null)
              }}
            />
            <span style={{ marginLeft: 6 }}>파일 업로드</span>
          </label>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="sourceType"
              checked={sourceType === 'url'}
              onChange={() => {
                setSourceType('url')
                setResult(null)
              }}
            />
            <span style={{ marginLeft: 6 }}>S3 업로드된 URL</span>
          </label>
        </div>

        {sourceType === 'file' ? (
          <div style={{ marginTop: 12 }}>
            <input
              type="file"
              accept={currentAccept}
              onChange={e => {
                setFile(e.target.files[0] || null)
                setResult(null)
              }}
            />
            {file && (
              <div style={styles.hint}>
                {file.name} ({formatBytes(file.size)})
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <input
              type="text"
              value={url}
              placeholder="https://img.stipop.io/..."
              onChange={e => {
                setUrl(e.target.value)
                setResult(null)
              }}
              style={styles.textInput}
            />
          </div>
        )}
      </div>

      <div style={styles.card}>
        <div style={styles.label}>변수</div>
        <div style={styles.varRow}>
          <div style={styles.varItem}>
            <div style={styles.varLabel}>quality (0~100)</div>
            <input
              type="number"
              min={0}
              max={100}
              value={quality}
              onChange={e => setQuality(e.target.value)}
              style={styles.numInput}
            />
          </div>

          {isMp4Anim && (
            <div style={styles.varItem}>
              <div style={styles.varLabel}>fps</div>
              <input
                type="number"
                min={1}
                max={60}
                value={fps}
                onChange={e => setFps(e.target.value)}
                style={styles.numInput}
              />
            </div>
          )}

          <div style={styles.varItem}>
            <div style={styles.varLabel}>width (선택)</div>
            <input
              type="number"
              min={1}
              value={width}
              placeholder="원본"
              onChange={e => setWidth(e.target.value)}
              style={styles.numInput}
            />
          </div>
          <div style={styles.varItem}>
            <div style={styles.varLabel}>height (선택)</div>
            <input
              type="number"
              min={1}
              value={height}
              placeholder="원본"
              onChange={e => setHeight(e.target.value)}
              style={styles.numInput}
            />
          </div>

          {isMp4Anim && (
            <div style={styles.varItem}>
              <div style={styles.varLabel}>영상 길이(초, 선택)</div>
              <input
                type="number"
                min={0}
                step="0.1"
                value={duration}
                placeholder="원본"
                onChange={e => setDuration(e.target.value)}
                style={styles.numInput}
              />
            </div>
          )}
        </div>
        <div style={styles.hint}>
          {isMp4Anim
            ? 'quality/fps를 낮추거나 길이를 줄이면 용량이 줄어듭니다. width/height 한쪽만 입력 시 비율 유지 축소.'
            : isImage
            ? 'width/height 입력 시 비율 유지 축소(미입력 시 원본 유지).'
            : 'mp4의 첫 프레임을 정지 webp로 추출합니다. width/height 입력 시 비율 유지 축소.'}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.label}>출력 방식</div>
        <div style={styles.radioRow}>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="output"
              checked={output === 'file'}
              onChange={() => onChangeOutput('file')}
            />
            <span style={{ marginLeft: 6 }}>webp 파일 다운로드</span>
          </label>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              name="output"
              checked={output === 's3'}
              onChange={() => onChangeOutput('s3')}
            />
            <span style={{ marginLeft: 6 }}>S3 업로드</span>
          </label>
        </div>

        {output === 's3' && (
          <div style={{ marginTop: 12 }}>
            <input
              type="text"
              value={s3Key}
              placeholder="예: fanza/scene/2026/7/1/name.webp (맨 앞 / 없이)"
              onChange={e => setS3Key(e.target.value)}
              style={styles.textInput}
            />
            <div style={styles.hint}>
              stipop 버킷 기준 경로(Key)입니다. 저장 후{' '}
              <code>https://img.stipop.io/[Key]</code> 로 접근됩니다.
            </div>
          </div>
        )}
      </div>

      <button
        onClick={convert}
        disabled={loading}
        style={{
          ...styles.button,
          opacity: loading ? 0.6 : 1,
          cursor: loading ? 'not-allowed' : 'pointer',
        }}
      >
        {loading ? '변환 중...' : 'WEBP로 변환'}
      </button>

      {result && (
        <div style={styles.card}>
          <div style={styles.label}>결과</div>
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div>
              <img
                src={result.objectUrl || result.s3Url}
                alt="webp result"
                style={styles.preview}
              />
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={styles.resultRow}>
                <span>원본</span>
                <b>{formatBytes(result.inputSize)}</b>
              </div>
              <div style={styles.resultRow}>
                <span>변환</span>
                <b>{formatBytes(result.outputSize)}</b>
              </div>
              {savedRatio !== null && (
                <div style={styles.resultRow}>
                  <span>절감율</span>
                  <b style={{ color: savedRatio >= 0 ? '#2e7d32' : '#c62828' }}>
                    {savedRatio}%
                  </b>
                </div>
              )}

              {result.s3Url ? (
                <div style={{ marginTop: 16 }}>
                  <div style={styles.varLabel}>업로드 URL</div>
                  <a
                    href={result.s3Url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ wordBreak: 'break-all', fontSize: 13 }}
                  >
                    {result.s3Url}
                  </a>
                  <div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(result.s3Url)
                        window.alert('URL이 복사되었습니다.')
                      }}
                      style={styles.downloadBtn}
                    >
                      URL 복사
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={download} style={styles.downloadBtn}>
                  다운로드
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  wrapper: {
    padding: '30px 40px',
    maxWidth: 900,
  },
  card: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    padding: 20,
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#333',
  },
  radioRow: {
    display: 'flex',
    gap: 24,
    flexWrap: 'wrap',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: 14,
  },
  textInput: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #ccc',
    borderRadius: 4,
    fontSize: 14,
  },
  varRow: {
    display: 'flex',
    gap: 20,
    flexWrap: 'wrap',
  },
  varItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  varLabel: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
  },
  numInput: {
    width: 120,
    padding: '6px 8px',
    border: '1px solid #ccc',
    borderRadius: 4,
    fontSize: 14,
  },
  hint: {
    marginTop: 10,
    fontSize: 12,
    color: '#888',
  },
  button: {
    background: '#357ca5',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '12px 28px',
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  preview: {
    maxWidth: 320,
    maxHeight: 320,
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    background:
      'repeating-conic-gradient(#f0f0f0 0% 25%, #fff 0% 50%) 50% / 20px 20px',
  },
  resultRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
    fontSize: 14,
  },
  downloadBtn: {
    marginTop: 16,
    background: '#2e7d32',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '10px 24px',
    fontSize: 14,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
}

export default WebpConverter
